import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "../components/Container/PageContainer";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  IconUsers,
  IconUpload,
  IconFolders,
  IconBook2,
  IconClock,
  IconPlus,
  IconListDetails,
  IconChartBar,
  IconBuildingStore,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

const iconByKey = {
  userList: IconUsers,
  avatarUpload: IconUpload,
  collectionManager: IconFolders,
  monthlyMagazine: IconBook2,
  manualLifetimeThreads: IconClock,
  createCollection: IconPlus,
  taskLogs: IconListDetails,
  visualization: IconChartBar,
  cafeManager: IconBuildingStore,
  textAttachmentBackfill: IconListDetails,
  threadsPeople: IconUsers,
};

const AdminHome = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const adminSections = useMemo(
    () => [
      {
        key: "userList",
        path: "/admin/user-list",
      },
      {
        key: "threadsPeople",
        path: "/admin/threads-people",
      },
      {
        key: "avatarUpload",
        path: "/admin/avatar-upload",
      },
      {
        key: "collectionManager",
        path: "/admin/collection-manager",
      },
      {
        key: "monthlyMagazine",
        path: "/admin/monthly-magazine",
      },
      {
        key: "manualLifetimeThreads",
        path: "/admin/manual-lifetime-threads",
      },
      {
        key: "createCollection",
        path: "/admin/create-collection",
      },
      {
        key: "taskLogs",
        path: "/admin/task-logs",
      },
      {
        key: "visualization",
        path: "/admin/visualization",
      },
      {
        key: "cafeManager",
        path: "/admin/cafe-manager",
      },
      {
        key: "textAttachmentBackfill",
        path: "/admin/text-attachment-backfill",
      },
    ],
    []
  );

  return (
    <PageContainer expand>
      <Stack spacing={4} sx={{ width: "100%" }}>
        <Stack spacing={1}>
          <Typography variant="h4" fontWeight={600}>
            {t("adminLanding.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("adminLanding.subtitle")}
          </Typography>
        </Stack>

        <Grid container spacing={{ xs: 1, md: 2 }}>
          {adminSections.map(({ key, path }) => {
            const IconComponent = iconByKey[key];
            const title = t(`adminLanding.cards.${key}.title`);
            const description = t(`adminLanding.cards.${key}.description`);

            return (
              <Grid key={key} item size={{ xs: 6, sm: 6, md: 3 }}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    background:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.background.paper, 0.7)
                        : theme.palette.background.paper,
                    boxShadow: `0 20px 40px -24px ${alpha(
                      theme.palette.common.black,
                      0.12
                    )}`,
                  }}
                >
                  <CardActionArea
                    sx={{ height: "100%", display: "block" }}
                    onClick={() => navigate(path)}
                  >
                    <CardContent>
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              theme.palette.mode === "dark" ? 0.25 : 0.08
                            ),
                            color: theme.palette.primary.main,
                          }}
                        >
                          {IconComponent ? (
                            <IconComponent size={isSmDown ? 24 : 28} stroke={1.6} />
                          ) : null}
                        </Box>
                        <Stack spacing={1}>
                          <Typography variant="h6" fontWeight={600}>
                            {title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.6 }}
                          >
                            {description}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Stack>
    </PageContainer>
  );
};

export default AdminHome;
